import React, { useState, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  ToggleButtonGroup,
  ToggleButton,
  Fade,
  Tooltip
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import BarChartIcon from '@mui/icons-material/BarChart';
import PieChartIcon from '@mui/icons-material/PieChart';
import WorkIcon from '@mui/icons-material/Work';
import LocationCityIcon from '@mui/icons-material/LocationCity';
import './EmployeeAnalytics.css';

const COLORS = ['#003366', '#2196F3', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899'];
const DAYS_OF_WEEK = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const EmployeeAnalytics = ({ employees, workOrders, employeeWorkload, customers = [] }) => {
  const [activeView, setActiveView] = useState('all');

  // Workload Distribution Data
  const workloadData = useMemo(() => {
    return employees
      .map(emp => ({
        name: emp.name || `${emp.empObject?.firstName || ''} ${emp.empObject?.lastName || ''}`.trim() || 'Unknown',
        workload: employeeWorkload[emp.id] || 0
      }))
      .filter(emp => emp.workload > 0)
      .sort((a, b) => b.workload - a.workload)
      .slice(0, 10); // Top 10 employees
  }, [employees, employeeWorkload]);

  // Employee Distribution by City
  const routeCoverageData = useMemo(() => {
    const cityMap = {};

    employees.forEach(employee => {
      const city = employee.empObject?.empCity || 'Unknown';
      cityMap[city] = (cityMap[city] || 0) + 1;
    });

    // Convert to array and sort by employee count (descending)
    return Object.entries(cityMap)
      .map(([city, employees]) => ({
        city,
        employees
      }))
      .sort((a, b) => b.employees - a.employees)
      .slice(0, 10); // Top 10 cities
  }, [employees]);

  // Performance Comparison (based on workload completion)
  const performanceData = useMemo(() => {
    return employees
      .map(emp => ({
        name: emp.name || `${emp.empObject?.firstName || ''} ${emp.empObject?.lastName || ''}`.trim() || 'Unknown',
        completed: employeeWorkload[emp.id] || 0
      }))
      .filter(emp => emp.completed > 0)
      .sort((a, b) => b.completed - a.completed)
      .slice(0, 8); // Top 8 performers
  }, [employees, employeeWorkload]);

  // Designation Distribution
  const designationData = useMemo(() => {
    const designations = {};
    employees.forEach(emp => {
      const designation = emp.empObject?.empDesignation || 'Unassigned';
      designations[designation] = (designations[designation] || 0) + 1;
    });

    return Object.entries(designations).map(([name, value]) => ({
      name,
      value
    }));
  }, [employees]);

  const handleViewChange = (event, newView) => {
    if (newView !== null) {
      setActiveView(newView);
    }
  };

  const renderWorkloadChart = () => (
    <Fade in={true} timeout={600}>
      <Paper
        elevation={2}
        sx={{
          p: { xs: 2, sm: 3 },
          borderRadius: 2,
          height: '100%',
          transition: 'all 0.3s ease',
          '&:hover': {
            boxShadow: 4
          }
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <WorkIcon sx={{ color: '#003366', fontSize: { xs: 20, sm: 24 } }} />
          <Box>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: '#003366',
                fontSize: { xs: '1rem', sm: '1.25rem' }
              }}
            >
              Workload Distribution
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                fontSize: { xs: '0.7rem', sm: '0.75rem' }
              }}
            >
              Number of assigned routes per employee
            </Typography>
          </Box>
        </Box>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={workloadData} margin={{ top: 10, right: 10, left: -10, bottom: 20 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="name"
              angle={-45}
              textAnchor="end"
              height={80}
              tick={{ fontSize: window.innerWidth < 600 ? 10 : 12 }}
            />
            <YAxis tick={{ fontSize: window.innerWidth < 600 ? 10 : 12 }} />
            <RechartsTooltip
              contentStyle={{
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                border: '1px solid #ccc',
                borderRadius: 8,
                fontSize: window.innerWidth < 600 ? '0.75rem' : '0.875rem'
              }}
            />
            <Legend wrapperStyle={{ fontSize: window.innerWidth < 600 ? '0.75rem' : '0.875rem' }} />
            <Bar dataKey="workload" fill="#2196F3" name="Routes" />
          </BarChart>
        </ResponsiveContainer>
      </Paper>
    </Fade>
  );

  const renderRouteCoverageChart = () => (
    <Fade in={true} timeout={800}>
      <Paper
        elevation={2}
        sx={{
          p: { xs: 2, sm: 3 },
          borderRadius: 2,
          height: '100%',
          transition: 'all 0.3s ease',
          '&:hover': {
            boxShadow: 4
          }
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <LocationCityIcon sx={{ color: '#10B981', fontSize: { xs: 20, sm: 24 } }} />
          <Box>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: '#003366',
                fontSize: { xs: '1rem', sm: '1.25rem' }
              }}
            >
              Employees by City
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                fontSize: { xs: '0.7rem', sm: '0.75rem' }
              }}
            >
              Employee distribution by location
            </Typography>
          </Box>
        </Box>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={routeCoverageData} margin={{ top: 10, right: 10, left: -10, bottom: 60 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="city"
              angle={-45}
              textAnchor="end"
              height={80}
              tick={{ fontSize: window.innerWidth < 600 ? 10 : 12 }}
            />
            <YAxis
              tick={{ fontSize: window.innerWidth < 600 ? 10 : 12 }}
              allowDecimals={false}
              domain={[0, 'auto']}
            />
            <RechartsTooltip
              contentStyle={{
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                border: '1px solid #ccc',
                borderRadius: 8,
                fontSize: window.innerWidth < 600 ? '0.75rem' : '0.875rem'
              }}
            />
            <Legend wrapperStyle={{ fontSize: window.innerWidth < 600 ? '0.75rem' : '0.875rem' }} />
            <Bar dataKey="employees" fill="#10B981" name="Employees" />
          </BarChart>
        </ResponsiveContainer>
      </Paper>
    </Fade>
  );

  const renderPerformanceChart = () => (
    <Fade in={true} timeout={1000}>
      <Paper
        elevation={2}
        sx={{
          p: { xs: 2, sm: 3 },
          borderRadius: 2,
          height: '100%',
          transition: 'all 0.3s ease',
          '&:hover': {
            boxShadow: 4
          }
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <BarChartIcon sx={{ color: '#F59E0B', fontSize: { xs: 20, sm: 24 } }} />
          <Box>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: '#003366',
                fontSize: { xs: '1rem', sm: '1.25rem' }
              }}
            >
              Performance Comparison
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                fontSize: { xs: '0.7rem', sm: '0.75rem' }
              }}
            >
              Top performers by completed routes
            </Typography>
          </Box>
        </Box>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart
            data={performanceData}
            layout="vertical"
            margin={{ top: 10, right: 30, left: 80, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              type="number"
              tick={{ fontSize: window.innerWidth < 600 ? 10 : 12 }}
            />
            <YAxis
              dataKey="name"
              type="category"
              width={70}
              tick={{ fontSize: window.innerWidth < 600 ? 9 : 11 }}
            />
            <RechartsTooltip
              contentStyle={{
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                border: '1px solid #ccc',
                borderRadius: 8,
                fontSize: window.innerWidth < 600 ? '0.75rem' : '0.875rem'
              }}
            />
            <Legend wrapperStyle={{ fontSize: window.innerWidth < 600 ? '0.75rem' : '0.875rem' }} />
            <Bar dataKey="completed" fill="#F59E0B" name="Completed Routes" />
          </BarChart>
        </ResponsiveContainer>
      </Paper>
    </Fade>
  );

  const renderDesignationChart = () => (
    <Fade in={true} timeout={1200}>
      <Paper
        elevation={2}
        sx={{
          p: { xs: 2, sm: 3 },
          borderRadius: 2,
          height: '100%',
          transition: 'all 0.3s ease',
          '&:hover': {
            boxShadow: 4
          }
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <PieChartIcon sx={{ color: '#8B5CF6', fontSize: { xs: 20, sm: 24 } }} />
          <Box>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: '#003366',
                fontSize: { xs: '1rem', sm: '1.25rem' }
              }}
            >
              Designation Distribution
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                fontSize: { xs: '0.7rem', sm: '0.75rem' }
              }}
            >
              Employee breakdown by role
            </Typography>
          </Box>
        </Box>
        <ResponsiveContainer width="100%" height={300}>
          <PieChart>
            <Pie
              data={designationData}
              cx="50%"
              cy="50%"
              labelLine={false}
              label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
              outerRadius={window.innerWidth < 600 ? 70 : 90}
              fill="#8884d8"
              dataKey="value"
            >
              {designationData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
              ))}
            </Pie>
            <RechartsTooltip
              contentStyle={{
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                border: '1px solid #ccc',
                borderRadius: 8,
                fontSize: window.innerWidth < 600 ? '0.75rem' : '0.875rem'
              }}
            />
          </PieChart>
        </ResponsiveContainer>
      </Paper>
    </Fade>
  );

  return (
    <Box sx={{ mt: 3, mb: 3 }}>
      {/* Header with Toggle */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', sm: 'center' },
          mb: 3,
          gap: 2
        }}
      >
        <Typography
          variant="h5"
          sx={{
            fontWeight: 600,
            color: '#003366',
            fontSize: { xs: '1.25rem', sm: '1.5rem' }
          }}
        >
          Employee Performance Analytics
        </Typography>
        <ToggleButtonGroup
          value={activeView}
          exclusive
          onChange={handleViewChange}
          size={window.innerWidth < 600 ? 'small' : 'medium'}
          sx={{
            backgroundColor: 'white',
            '& .MuiToggleButton-root': {
              fontSize: { xs: '0.7rem', sm: '0.875rem' },
              padding: { xs: '4px 8px', sm: '6px 16px' }
            }
          }}
        >
          <ToggleButton value="all">
            <Tooltip title="View all analytics charts">
              <span>All Charts</span>
            </Tooltip>
          </ToggleButton>
          <ToggleButton value="workload">
            <Tooltip title="View workload distribution">
              <span>Workload</span>
            </Tooltip>
          </ToggleButton>
          <ToggleButton value="performance">
            <Tooltip title="View performance metrics">
              <span>Performance</span>
            </Tooltip>
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Charts Grid */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            md: activeView === 'all' ? 'repeat(2, 1fr)' : '1fr'
          },
          gap: { xs: 2, sm: 3 }
        }}
      >
        {(activeView === 'all' || activeView === 'workload') && renderWorkloadChart()}
        {(activeView === 'all' || activeView === 'workload') && renderRouteCoverageChart()}
        {(activeView === 'all' || activeView === 'performance') && renderPerformanceChart()}
        {(activeView === 'all' || activeView === 'performance') && renderDesignationChart()}
      </Box>
    </Box>
  );
};

export default EmployeeAnalytics;
